import { Phone, Mail, MapPin, MapPinned, MessageCircle, Facebook, Instagram, Youtube, Linkedin, Twitter, Globe } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const ICONS = {
  phone: Phone, whatsapp: MessageCircle, email: Mail, address: MapPin, maps: MapPinned,
  facebook: Facebook, instagram: Instagram, youtube: Youtube, linkedin: Linkedin,
  twitter: Twitter, other: Globe,
};
export const CONTACT_ICONS = ICONS;

const VIS_KEY = { home: "show_home", contact: "show_contact", footer: "show_footer" };
const LINKLESS = new Set(["address"]);

export function getVisibleItems(settings, page) {
  const key = VIS_KEY[page];
  return (settings.contact_items || [])
    .filter((i) => i.enabled && i[key] && (i.value || "").trim())
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export function itemHref(item) {
  const v = (item.value || "").trim();
  if (!v || LINKLESS.has(item.key)) return null;
  if (item.key === "phone1" || item.key === "phone2") return `tel:${v.replace(/[^\d+]/g, "")}`;
  if (item.key === "whatsapp") {
    const d = v.replace(/\D/g, "");
    return `https://wa.me/${d.length === 10 ? "91" + d : d}`;
  }
  if (item.key === "email") return `mailto:${v}`;
  return v; // maps + social URLs
}

const isExternal = (item) => item.kind === "social" || item.key === "maps";
const linkProps = (item) => (isExternal(item) ? { target: "_blank", rel: "noopener noreferrer" } : {});

function Value({ item, className }) {
  const href = itemHref(item);
  const label = item.key === "maps" ? "View on Google Maps" : item.value;
  if (!href) return <span className={className}>{label}</span>;
  return <a href={href} {...linkProps(item)} className={className}>{label}</a>;
}

export function WhatsAppBubble() {
  const { settings } = useSettings();
  const item = (settings.contact_items || []).find((i) => i.key === "whatsapp" && i.enabled && (i.value || "").trim());
  if (!item) return null;
  return (
    <a
      href={itemHref(item)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-bubble"
      aria-label="Chat on WhatsApp"
      className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-whatsapp text-white shadow-lg shadow-emerald-900/30 transition-transform hover:scale-110"
    >
      <MessageCircle size={28} />
      <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-emerald-400" />
      </span>
    </a>
  );
}

export function ContactStrip({ page = "home" }) {
  const { settings } = useSettings();
  const items = getVisibleItems(settings, page);
  if (!items.length) return null;
  return (
    <div data-testid={`contact-strip-${page}`} className="flex flex-wrap items-center gap-2">
      {items.map((item) => {
        const Icon = ICONS[item.icon] || Globe;
        const href = itemHref(item);
        const cls = "inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20";
        const inner = (<><Icon size={13} className="text-orange" /><span>{item.key === "maps" ? "Google Maps" : item.label}</span></>);
        return href
          ? <a key={item.id} href={href} {...linkProps(item)} data-testid={`strip-${item.key}`} className={cls}>{inner}</a>
          : <span key={item.id} data-testid={`strip-${item.key}`} className={cls}>{inner}</span>;
      })}
    </div>
  );
}

export function ContactFooterBlock() {
  const { settings } = useSettings();
  const items = getVisibleItems(settings, "footer");
  const contacts = items.filter((i) => i.kind === "contact");
  const socials = items.filter((i) => i.kind === "social");
  return (
    <div data-testid="footer-contact-items">
      {contacts.length > 0 && (
        <ul className="space-y-3 text-sm text-slate-400">
          {contacts.map((item) => {
            const Icon = ICONS[item.icon] || Globe;
            return (
              <li key={item.id} data-testid={`footer-item-${item.key}`} className="flex gap-2">
                <Icon size={16} className="mt-0.5 shrink-0 brand-accent" />
                <Value item={item} className="hover:text-white" />
              </li>
            );
          })}
        </ul>
      )}
      {socials.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {socials.map((item) => {
            const Icon = ICONS[item.icon] || Globe;
            return (
              <a key={item.id} href={itemHref(item)} {...linkProps(item)} data-testid={`footer-social-${item.key}`} title={item.label}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 text-slate-400 transition-colors hover:border-brand hover:text-white">
                <Icon size={16} />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ContactCards() {
  const { settings } = useSettings();
  const items = getVisibleItems(settings, "contact");
  const contacts = items.filter((i) => i.kind === "contact");
  const socials = items.filter((i) => i.kind === "social");
  return (
    <div data-testid="contact-page-items">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {contacts.map((item) => {
          const Icon = ICONS[item.icon] || Globe;
          return (
            <div key={item.id} data-testid={`contact-card-${item.key}`} className="rounded-2xl border border-slate-200 bg-white p-6">
              <Icon className="mb-3 brand-accent" size={26} />
              <h3 className="font-display font-bold text-slate-900">{item.label}</h3>
              <Value item={item} className="mt-1 block break-all text-sm text-slate-600 hover:text-brand" />
            </div>
          );
        })}
      </div>
      {socials.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="contact-socials">
          <span className="text-sm font-semibold text-slate-500">Follow us:</span>
          {socials.map((item) => {
            const Icon = ICONS[item.icon] || Globe;
            return (
              <a key={item.id} href={itemHref(item)} {...linkProps(item)} data-testid={`contact-social-${item.key}`} title={item.label}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-brand hover:text-brand">
                <Icon size={18} />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
